/**
 * The discussion on one row.
 *
 * Paged rather than fetched whole, the same way notifications are: the list is
 * unbounded and the panel shows a screenful. The realtime layer marks this key
 * stale rather than patching it -- see `features/realtime/handlers/database.ts`
 * -- so a row nobody has open costs a flag and no request.
 *
 * **Nothing here guesses at a comment.** Two of the three writes cannot be
 * guessed at: a new comment is a row the server names, and both writing and
 * editing send plain text that the server turns into a document and renders
 * back, mentions and all. Deleting could be optimistic and is not, because it
 * would then be the one thing in the panel behaving differently for the sake of
 * a round trip nobody is waiting on.
 *
 * The list arrives newest first, which is the endpoint's own order and the one
 * the panel shows. Older comments are asked for by offset, into a list that
 * grows at the *front* -- so a page boundary can repeat an entry that a new
 * comment has pushed across it. `mergedPages` drops the repeat.
 */
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { RowComment } from '@/api/types'

export const COMMENT_PAGE_SIZE = 20

export interface CommentPage {
  count: number
  next: string | null
  previous: string | null
  results: RowComment[]
}

type Pages = InfiniteData<CommentPage, number>

/**
 * The comments on one row.
 *
 * The endpoint takes an optional `view`, which lets it fall back to a view's
 * permissions when the table's refuse. It is not sent, and cannot help here:
 * the fallback is only reached when the view's ownership type allows rows to be
 * read through it, and `collaborative` -- the one ownership type this fork
 * registers -- refuses. Sending it would be a parameter that never changes an
 * answer.
 */
export function rowCommentsQuery(tableId: number, rowId: number) {
  return infiniteQueryOptions({
    queryKey: qk.rowComments(tableId, rowId),
    queryFn: ({ pageParam }): Promise<CommentPage> =>
      unwrap(
        getApi().GET('/api/database/rows/table/{table_id}/{row_id}/comments/', {
          params: {
            path: { table_id: tableId, row_id: rowId },
            query: { limit: COMMENT_PAGE_SIZE, offset: pageParam },
          },
        }),
      ) as Promise<CommentPage>,
    initialPageParam: 0,
    getNextPageParam: (last, _pages, lastOffset) =>
      last.next === null ? undefined : lastOffset + COMMENT_PAGE_SIZE,
  })
}

export function useRowComments(tableId: number, rowId: number) {
  return useInfiniteQuery(rowCommentsQuery(tableId, rowId))
}

/**
 * Applies a change to the pages this row's comments are held in.
 *
 * Nothing is written when the query holds nothing: the panel is what mounts it,
 * so a write with no pages behind it is a race with the first read, and putting
 * a lone comment in an empty cache would show it as the whole discussion.
 */
function patchComments(
  queryClient: QueryClient,
  tableId: number,
  rowId: number,
  update: (comments: RowComment[]) => RowComment[],
): void {
  queryClient.setQueryData<Pages>(qk.rowComments(tableId, rowId), (held) =>
    held === undefined
      ? held
      : {
          ...held,
          pages: held.pages.map((page) => ({
            ...page,
            results: update(page.results),
          })),
        },
  )
}

/** Puts a comment the server has just named at the head of the first page. */
function prependComment(
  queryClient: QueryClient,
  tableId: number,
  rowId: number,
  comment: RowComment,
): void {
  queryClient.setQueryData<Pages>(qk.rowComments(tableId, rowId), (held) => {
    const [first, ...rest] = held?.pages ?? []
    if (held === undefined || first === undefined) return held
    return {
      ...held,
      pages: [{ ...first, results: [comment, ...first.results] }, ...rest],
    }
  })
}

export interface CommentActions {
  post: (message: string) => void
  edit: (commentId: number, message: string) => void
  remove: (commentId: number) => void
  /** True while a comment is being written, so the composer can say so. */
  isPosting: boolean
}

export function useCommentActions(tableId: number, rowId: number): CommentActions {
  const queryClient = useQueryClient()

  const write = useMutation({
    mutationFn: (message: string): Promise<RowComment> =>
      unwrap(
        getApi().POST('/api/database/rows/table/{table_id}/{row_id}/comments/', {
          params: { path: { table_id: tableId, row_id: rowId } },
          // Plain text, which the endpoint turns into a ProseMirror document.
          // The document form exists for mentions, which this build has no way
          // to compose.
          body: { message },
        }),
      ),
    onSuccess: (comment) => {
      prependComment(queryClient, tableId, rowId, comment)
    },
  })

  const change = useMutation({
    mutationFn: ({
      commentId,
      message,
    }: {
      commentId: number
      message: string
    }): Promise<RowComment> =>
      unwrap(
        getApi().PATCH('/api/database/rows/table/{table_id}/comments/{comment_id}/', {
          params: { path: { table_id: tableId, comment_id: commentId } },
          body: { message },
        }),
      ),
    onSuccess: (comment) => {
      patchComments(queryClient, tableId, rowId, (comments) =>
        comments.map((held) => (held.id === comment.id ? comment : held)),
      )
    },
  })

  const destroy = useMutation({
    mutationFn: (commentId: number) =>
      unwrap(
        getApi().DELETE('/api/database/rows/table/{table_id}/comments/{comment_id}/', {
          params: { path: { table_id: tableId, comment_id: commentId } },
        }),
      ),
    onSuccess: (_answer, commentId) => {
      patchComments(queryClient, tableId, rowId, (comments) =>
        comments.filter((held) => held.id !== commentId),
      )
    },
  })

  return {
    post: (message) => {
      write.mutate(message)
    },
    edit: (commentId, message) => {
      change.mutate({ commentId, message })
    },
    remove: (commentId) => {
      destroy.mutate(commentId)
    },
    isPosting: write.isPending,
  }
}
